import { useApolloClient, useMutation } from "@apollo/react-hooks";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ApolloError } from "apollo-client/errors/ApolloError";
import { isApolloError } from "apollo-client/errors/ApolloError";
import React, { ReactNode, SyntheticEvent, useEffect, useReducer } from "react";
import UserSelect, {
  SelectedUserEvent,
  UserOption,
} from "../../../../components/user/UserSelect";
import { ValidationErrorReasons } from "../../../../graphql/errors";
import {
  ASSIGN_OWNER,
  ORDER_OWNER,
  OrderOwner,
  OrderOwnerResult,
  REVOKE_OWNER,
  RevokeOwnerResult,
} from "../../../../api/rvorderowner";
import { UserSelectProps } from "./AssignOwnerModal";

export interface ReleaseOwnerModalProps {
  orderID: string;
  // shown in the title
  orderName?: string;
  show: boolean;
  // the dialog was closed, whether cancelled or submitted
  onDone: () => void;
  // the owner was released, or the order reassigned
  onChanged?: () => void;
  userSelect?: (props: UserSelectProps) => ReactNode;
}

interface State {
  showDialog?: boolean;
  showAlert?: boolean;
  submitting?: boolean;
  alertSeverity?: "success" | "error";
  alertMessage?: string;
  selectedUser?: UserOption;
  // the owner is looked up each time the dialog is shown, so that a previous
  // showing's answer is never offered: null when the order has none
  lookup?: "loading" | "failed";
  owner?: OrderOwner | null;
}

type Action =
  | { type: "hidden" }
  | { type: "show" }
  | { type: "owner_found"; owner: OrderOwner | null }
  | { type: "lookup_failed" }
  | { type: "selected_user"; user: UserOption }
  | { type: "cleared_user" }
  | { type: "submitting" }
  | { type: "success"; message: string }
  | { type: "failure"; message: string };

const reducer = (prev: State, action: Action): State => {
  switch (action.type) {
    case "show":
      return { showDialog: true, lookup: "loading" };
    case "owner_found":
      return { ...prev, lookup: undefined, owner: action.owner };
    case "lookup_failed":
      return { ...prev, lookup: "failed" };
    case "selected_user":
      return { ...prev, selectedUser: action.user };
    case "cleared_user":
      return { ...prev, selectedUser: undefined };
    case "submitting":
      return { ...prev, showDialog: false, submitting: true };
    case "success":
      return {
        showAlert: true,
        alertSeverity: "success",
        alertMessage: action.message,
      };
    case "failure":
      return {
        showAlert: true,
        alertSeverity: "error",
        alertMessage: action.message,
      };
    case "hidden":
    default:
      return {};
  }
};

// the reasons the backend gives for refusing, e.g. "Not the owner of this
// order", apart from the message; or the fallback when there are none
const failureMessage = (e: unknown, fallback: string): string => {
  if (!isApolloError(e as Error)) {
    console.error(e);
    return fallback;
  }
  const apolloError = e as ApolloError;
  const reasons: string[] = ValidationErrorReasons(apolloError);
  const messages =
    reasons.length > 0
      ? reasons
      : apolloError.graphQLErrors.map(({ message }) => message);
  return messages.length > 0 ? messages.join(". ") : fallback;
};

// Releases an order from its owner, or reassigns it to another user, on behalf
// of the owner. Needs 'rv_owner_list' to find the owner, 'rv_owner_revoke' to
// release, and 'rv_owner_assign' as well to reassign: a user_admin.
export const ReleaseOwnerModal = ({
  orderID,
  orderName,
  show,
  onDone,
  onChanged,
  userSelect = UserSelect,
}: ReleaseOwnerModalProps): JSX.Element => {
  // state
  const [state, dispatch] = useReducer(reducer, {} as State);

  // the current owner, looked up afresh each time the dialog is shown
  const client = useApolloClient();
  useEffect(() => {
    if (!show || "" === orderID) {
      dispatch({ type: "hidden" });
      return;
    }
    dispatch({ type: "show" });
    let cancelled = false;
    client
      .query<OrderOwnerResult>({
        query: ORDER_OWNER,
        variables: { orderID },
        fetchPolicy: "no-cache",
      })
      .then(({ data }) => {
        if (!cancelled) {
          dispatch({ type: "owner_found", owner: data.rvOrderOwner });
        }
      })
      .catch((e) => {
        console.error("failed to look up the order owner", e);
        if (!cancelled) dispatch({ type: "lookup_failed" });
      });
    return (): void => {
      cancelled = true;
    };
  }, [show, orderID, client]);
  const loading = "loading" === state.lookup;
  const error = "failed" === state.lookup;
  const owner = state.owner || undefined;

  // handlers
  const handleClose = (): void => onDone();
  const handleUserChange = (e: SelectedUserEvent): void => {
    dispatch({ type: "selected_user", user: e.user });
  };
  // back to releasing rather than reassigning
  const handleUserClear = (): void => {
    dispatch({ type: "cleared_user" });
  };
  const [revokeOwner] = useMutation<RevokeOwnerResult>(REVOKE_OWNER);
  const [assignOwner] = useMutation(ASSIGN_OWNER);
  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    if (!owner) return;
    const target = state.selectedUser;
    dispatch({ type: "submitting" });
    onDone();
    try {
      await revokeOwner({
        variables: { userID: owner.id, orderID },
      });
    } catch (e) {
      dispatch({
        type: "failure",
        message: failureMessage(e, "Failed to release order"),
      });
      return;
    }
    if (!target) {
      dispatch({ type: "success", message: "Released order OK" });
      onChanged?.();
      return;
    }
    try {
      await assignOwner({
        variables: { userID: target.user.id, orderID },
      });
      dispatch({ type: "success", message: "Reassigned order OK" });
    } catch (e) {
      // the order is now unowned; say so rather than just "failed"
      dispatch({
        type: "failure",
        message: `Released the order, but failed to assign it: ${failureMessage(
          e,
          "unknown error"
        )}`,
      });
    }
    onChanged?.();
  };
  const handleHideAlert = (): void => dispatch({ type: "hidden" });

  let text: ReactNode;
  if (loading) {
    text = "Looking up the owner…";
  } else if (error) {
    text = "Failed to look up the owner.";
  } else if (!owner) {
    text = "This order has no owner. Use Assign to give it one.";
  } else {
    text = (
      <>
        Owned by <strong>{owner.email}</strong>. Release the order to leave it
        without an owner, or pick a user to reassign it to. Clear the user with
        its × to release instead.
      </>
    );
  }
  const title = orderName
    ? `Release order '${orderName}' from its owner`
    : "Release order from its owner";

  // called on every render, whether or not it is shown: the picker is a
  // function whose hooks count as this component's, so calling it only once
  // the owner is known changes the hook order and React throws (error 310)
  const picker = userSelect({
    isDisabled: state.submitting === true,
    onSelectUser: handleUserChange,
    isClearable: true,
    onClear: handleUserClear,
    // reassigning to the current owner would be a no-op
    excludeUserIDs: owner ? [owner.id] : undefined,
    value: state.selectedUser,
  });

  return (
    <>
      <Snackbar
        open={state.showAlert === true}
        autoHideDuration={6000}
        onClose={handleHideAlert}
        data-testid="alerts"
      >
        <Alert severity={state.alertSeverity}>{state.alertMessage}</Alert>
      </Snackbar>
      <Dialog
        open={state.showDialog === true}
        onClose={handleClose}
        data-testid="dialog-root"
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="owner-text">{text}</DialogContentText>
          {owner && picker}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!owner || state.submitting === true}
          >
            {state.selectedUser ? "Reassign" : "Release"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
